import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    LOCALE_ID,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
    RegisterUser,
    RegistrationPolicy,
    UsernameAvailability,
} from '../models';
import { AuthService } from './../auth.service';
import { MainConfig } from '../../../conf/main.config';

declare global {
    interface Window {
        hcaptcha: any;
    }
}
window.hcaptcha = window.hcaptcha || null;

type UsernameStatus =
    | 'idle'
    | 'checking'
    | 'available'
    | 'unavailable'
    | 'invalid'
    | 'error';

@Component({
    selector: 'register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.css'],
})
export class RegisterComponent implements OnInit, AfterViewInit {
    readonly MainConfig = MainConfig;

    @ViewChild('stepTitle', { static: false }) stepTitle: ElementRef;
    @ViewChild('emailInput', { static: false }) emailInput: ElementRef;

    user: RegisterUser = new RegisterUser();
    confirmPassword = '';

    step = 1;
    currentLanguage = 'pt-BR';

    policy: RegistrationPolicy = null;
    policyLoading = true;
    policyError = '';

    step1Tried = false;
    step2Tried = false;
    submitting = false;

    showPassword = false;
    showConfirmPassword = false;

    usernameStatus: UsernameStatus = 'idle';
    usernameAvailabilityMessage = '';
    private usernameCheckSequence = 0;

    errorMessage = '';
    successMessage = '';
    successState = false;
    emailConfirmationRequired = false;
    emailConfirmationSent = false;
    avatarUploadFailed = false;

    userAvatar = '';
    avatarFileName = '';
    avatarError = '';

    locale: string;

    constructor(
        @Inject(LOCALE_ID) readonly localeId: string,
        private auth: AuthService,
        private router: Router,
        public activeModal: NgbActiveModal
    ) {
        this.locale = localeId;
        this.currentLanguage =
            localStorage.getItem('language') === 'fr' ? 'fr' : 'pt-BR';

        this.user.termsAccepted = false;
        this.user.privacyAcknowledged = false;
        this.user.scientificLicenseAccepted = false;
    }

    ngOnInit(): void {
        this.loadRegistrationPolicy();
    }

    ngAfterViewInit(): void {
        this.loadCaptchaScript();
        setTimeout(() => this.focusStepTitle(), 0);
    }

    t(portuguese: string, french: string): string {
        return this.currentLanguage === 'fr' ? french : portuguese;
    }

    setLanguage(language: 'pt-BR' | 'fr'): void {
        this.currentLanguage = language;
        this.locale = language;
        localStorage.setItem('language', language);
        document.documentElement.lang = language;
    }

    get passwordMinLength(): number {
        return this.policy && this.policy.password
            ? this.policy.password.minLength
            : 10;
    }

    get usernameMinLength(): number {
        return this.policy && this.policy.username
            ? this.policy.username.minLength
            : 3;
    }

    get usernameMaxLength(): number {
        return this.policy && this.policy.username
            ? this.policy.username.maxLength
            : 40;
    }

    get usernamePattern(): string {
        return this.policy && this.policy.username
            ? this.policy.username.pattern
            : '^[A-Za-z0-9._-]{3,40}$';
    }

    get avatarMaxBytes(): number {
        return this.policy && this.policy.avatar
            ? this.policy.avatar.maxBytes
            : 5 * 1024 * 1024;
    }

    get avatarLimitMb(): number {
        return Math.max(1, Math.round(this.avatarMaxBytes / 1024 / 1024));
    }

    get avatarFormatsLabel(): string {
        if (
            this.policy &&
            this.policy.avatar &&
            this.policy.avatar.extensions &&
            this.policy.avatar.extensions.length
        ) {
            return this.policy.avatar.extensions
                .map((extension) => extension.toUpperCase())
                .join(', ');
        }
        return 'JPG, JPEG, PNG';
    }

    get termsUrl(): string {
        return this.policy && this.policy.terms
            ? this.policy.terms.url
            : '';
    }

    get privacyConfigured(): boolean {
        return !!(this.policy && this.policy.privacy);
    }

    get communityGuidelinesConfigured(): boolean {
        return !!(this.policy && this.policy.communityGuidelines);
    }

    get scientificLicenseConfigured(): boolean {
        return !!(this.policy && this.policy.scientificLicense);
    }

    get passwordsMatch(): boolean {
        return (
            !!this.user.password &&
            !!this.confirmPassword &&
            this.user.password === this.confirmPassword
        );
    }

    loadRegistrationPolicy(): void {
        this.policyLoading = true;
        this.policyError = '';

        this.auth.getRegistrationPolicy().subscribe(
            (policy: RegistrationPolicy) => {
                this.policy = policy;
                this.policyLoading = false;

                if (policy && policy.captchaRequired) {
                    if (!MainConfig.HCAPTCHA_SITE_KEY) {
                        this.policyError = this.t(
                            'O servidor exige captcha, mas a chave pública do hCaptcha não está configurada no frontend.',
                            'Le serveur exige un captcha, mais la clé publique hCaptcha n’est pas configurée dans le frontend.'
                        );
                    } else {
                        setTimeout(() => this.loadCaptchaScript(), 0);
                    }
                }

                if (policy && policy.scientificLicense) {
                    this.user.scientificLicenseCode =
                        policy.scientificLicense.code;
                } else {
                    this.user.scientificLicenseAccepted = false;
                    this.user.scientificLicenseCode = null;
                }
            },
            () => {
                this.policyLoading = false;
                this.policyError = this.t(
                    'Não foi possível carregar as regras reais de cadastro. Tente novamente antes de continuar.',
                    "Impossible de charger les règles réelles d'inscription. Réessayez avant de continuer."
                );
            }
        );
    }

    nextStep(accessForm: NgForm): void {
        this.step1Tried = true;
        this.errorMessage = '';

        if (this.policyLoading || this.policyError) {
            return;
        }

        if (!this.passwordsMatch) {
            this.focusFirstInvalid(accessForm, true);
            return;
        }

        if (
            this.policy &&
            this.policy.captchaRequired &&
            MainConfig.HCAPTCHA_SITE_KEY &&
            !this.user.captchaToken
        ) {
            this.errorMessage = this.t(
                'Confirme que você é uma pessoa antes de continuar.',
                'Veuillez confirmer que vous êtes une personne avant de continuer.'
            );
            return;
        }

        if (!accessForm.valid) {
            this.focusFirstInvalid(accessForm, false);
            return;
        }

        this.step = 2;
        this.step2Tried = false;
        setTimeout(() => this.focusStepTitle(), 0);
    }

    previousStep(focusEmail: boolean = false): void {
        this.step = 1;
        this.errorMessage = '';
        setTimeout(() => {
            if (
                this.policy &&
                this.policy.captchaRequired &&
                MainConfig.HCAPTCHA_SITE_KEY
            ) {
                this.renderCaptcha();
            }
            if (focusEmail && this.emailInput) {
                this.emailInput.nativeElement.focus();
            } else {
                this.focusStepTitle();
            }
        }, 0);
    }

    editEmail(): void {
        this.previousStep(true);
    }

    togglePasswordVisibility(field: 'password' | 'confirm'): void {
        if (field === 'password') {
            this.showPassword = !this.showPassword;
        } else {
            this.showConfirmPassword = !this.showConfirmPassword;
        }
    }

    onUsernameInput(): void {
        this.usernameCheckSequence += 1;
        this.usernameStatus = 'idle';
        this.usernameAvailabilityMessage = '';
    }

    checkUsernameAvailability(): void {
        const username = String(this.user.username || '').trim();
        this.user.username = username;
        this.usernameCheckSequence += 1;
        const currentSequence = this.usernameCheckSequence;

        if (!username) {
            this.usernameStatus = 'idle';
            return;
        }

        let usernameRegex: RegExp;
        try {
            usernameRegex = new RegExp(this.usernamePattern);
        } catch (_error) {
            usernameRegex = /^[A-Za-z0-9._-]{3,40}$/;
        }

        if (
            username.length < this.usernameMinLength ||
            username.length > this.usernameMaxLength ||
            !usernameRegex.test(username)
        ) {
            this.usernameStatus = 'invalid';
            this.usernameAvailabilityMessage = this.t(
                'Use de 3 a 40 caracteres: letras, números, ponto, hífen ou sublinhado.',
                'Utilisez 3 à 40 caractères : lettres, chiffres, point, tiret ou soulignement.'
            );
            return;
        }

        this.usernameStatus = 'checking';
        this.usernameAvailabilityMessage = this.t(
            'Verificando disponibilidade…',
            'Vérification de la disponibilité…'
        );

        this.auth.checkUsernameAvailability(username).subscribe(
            (result: UsernameAvailability) => {
                if (currentSequence !== this.usernameCheckSequence) {
                    return;
                }

                if (result && result.available) {
                    this.usernameStatus = 'available';
                    this.usernameAvailabilityMessage = this.t(
                        'Nome de usuário disponível.',
                        "Nom d'utilisateur disponible."
                    );
                    return;
                }

                this.usernameStatus =
                    result && result.reason === 'invalid'
                        ? 'invalid'
                        : 'unavailable';
                this.usernameAvailabilityMessage =
                    (result && result.message) ||
                    this.t(
                        'Esse nome de usuário já está em uso.',
                        "Ce nom d'utilisateur est déjà utilisé."
                    );
            },
            () => {
                if (currentSequence !== this.usernameCheckSequence) {
                    return;
                }
                this.usernameStatus = 'error';
                this.usernameAvailabilityMessage = this.t(
                    'Não foi possível verificar agora. O servidor fará a validação final ao criar a conta.',
                    "La vérification est indisponible pour le moment. Le serveur effectuera la validation finale lors de la création du compte."
                );
            }
        );
    }

    onUploadAvatar(event: Event): void {
        this.avatarError = '';

        const input = event.target as HTMLInputElement;
        if (!input || !input.files || !input.files.length) {
            return;
        }

        const file = input.files[0];
        const allowedTypes = ['image/jpeg', 'image/png'];

        if (allowedTypes.indexOf(file.type) === -1) {
            this.avatarError = this.t(
                'Formato não aceito. Use JPG, JPEG ou PNG.',
                'Format non accepté. Utilisez JPG, JPEG ou PNG.'
            );
            input.value = '';
            return;
        }

        if (file.size > this.avatarMaxBytes) {
            this.avatarError = this.t(
                'A imagem excede o limite de ' + this.avatarLimitMb + ' MB.',
                "L'image dépasse la limite de " + this.avatarLimitMb + ' Mo.'
            );
            input.value = '';
            return;
        }

        const imageUrl = window.URL.createObjectURL(file);
        const image = document.createElement('img');

        image.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');

                if (!context) {
                    throw new Error('Canvas unavailable');
                }

                const maxHeight = MainConfig.imageUpload.maxHeight;
                const maxWidth = MainConfig.imageUpload.maxWidth;
                const scale = Math.min(
                    1,
                    maxWidth / image.width,
                    maxHeight / image.height
                );

                canvas.width = Math.max(1, Math.round(image.width * scale));
                canvas.height = Math.max(1, Math.round(image.height * scale));
                context.drawImage(image, 0, 0, canvas.width, canvas.height);

                const resizedImage = canvas.toDataURL(
                    'image/jpeg',
                    MainConfig.imageUpload.quality
                );

                this.userAvatar = resizedImage;
                this.user.avatar = resizedImage;
                this.user.extention = 'jpeg';
                this.avatarFileName = file.name;
            } catch (_error) {
                this.avatarError = this.t(
                    'Não foi possível processar essa imagem.',
                    "Impossible de traiter cette image."
                );
            } finally {
                window.URL.revokeObjectURL(imageUrl);
                input.value = '';
            }
        };

        image.onerror = () => {
            window.URL.revokeObjectURL(imageUrl);
            input.value = '';
            this.avatarError = this.t(
                'O arquivo selecionado não pôde ser lido como imagem.',
                "Le fichier sélectionné ne peut pas être lu comme une image."
            );
        };

        image.src = imageUrl;
    }

    removeAvatar(): void {
        this.userAvatar = '';
        this.avatarFileName = '';
        this.avatarError = '';
        this.user.avatar = null;
        this.user.extention = null;
    }

    onRegister(profileForm: NgForm): void {
        if (this.submitting) {
            return;
        }

        this.step2Tried = true;
        this.errorMessage = '';

        if (!profileForm.valid) {
            this.focusFirstInvalid(profileForm, false);
            return;
        }

        if (
            this.usernameStatus === 'unavailable' ||
            this.usernameStatus === 'invalid' ||
            this.usernameStatus === 'checking'
        ) {
            const usernameElement = document.getElementById('register-username');
            if (usernameElement) {
                usernameElement.focus();
            }
            return;
        }

        this.user.termsAccepted = this.user.termsAccepted === true;
        this.user.privacyAcknowledged =
            !this.policy ||
            !this.policy.privacy ||
            this.user.privacyAcknowledged === true;

        if (
            this.policy &&
            this.policy.scientificLicense &&
            this.user.scientificLicenseAccepted
        ) {
            this.user.scientificLicenseCode =
                this.policy.scientificLicense.code;
        } else {
            this.user.scientificLicenseAccepted = false;
            this.user.scientificLicenseCode = null;
        }

        this.submitting = true;

        this.auth.register(this.user).subscribe(
            (response: any) => {
                this.submitting = false;
                this.successState = true;
                this.successMessage =
                    (response && response.message) ||
                    this.t(
                        'Conta criada com sucesso.',
                        'Compte créé avec succès.'
                    );
                this.emailConfirmationRequired =
                    !!(response && response.email_confirmation_required);
                this.emailConfirmationSent =
                    !!(response && response.email_confirmation_sent);
                this.avatarUploadFailed =
                    !!(response && response.avatar_upload_failed);
                setTimeout(() => this.focusStepTitle(), 0);
            },
            (error) => {
                this.submitting = false;
                this.errorMessage = this.extractErrorMessage(error);
                this.focusStepTitle();
            }
        );
    }

    closeToHome(): void {
        this.activeModal.dismiss('home');
        this.router.navigate(['/home']);
    }

    openLogin(): void {
        // A barra superior recebe este motivo e abre o modal de login.
        // Assim o componente de cadastro não precisa conhecer o componente de login.
        this.activeModal.dismiss('switch-login');
    }

    private extractErrorMessage(error: any): string {
        if (error && error.error && error.error.message) {
            return error.error.message;
        }
        if (error && error.message) {
            return error.message;
        }
        return this.t(
            'Não foi possível criar a conta. Seus dados foram preservados; tente novamente.',
            "Impossible de créer le compte. Vos données ont été conservées ; réessayez."
        );
    }

    private focusStepTitle(): void {
        if (this.stepTitle && this.stepTitle.nativeElement) {
            this.stepTitle.nativeElement.focus();
        }
    }

    private focusFirstInvalid(form: NgForm, preferConfirmPassword: boolean): void {
        if (preferConfirmPassword) {
            const confirmElement = document.getElementById(
                'register-confirm-password'
            );
            if (confirmElement) {
                confirmElement.focus();
                return;
            }
        }

        if (!form || !form.form) {
            return;
        }

        Object.keys(form.controls).forEach((controlName) => {
            const control = form.controls[controlName];
            if (control && control.invalid) {
                control.markAsTouched();
            }
        });

        setTimeout(() => {
            const invalidElement = document.querySelector(
                '.wp-register-step.is-active input.ng-invalid, ' +
                    '.wp-register-step.is-active button.ng-invalid, ' +
                    '.wp-register-step.is-active [aria-invalid="true"]'
            ) as HTMLElement;

            if (invalidElement) {
                invalidElement.focus();
            }
        }, 0);
    }

    private loadCaptchaScript(): void {
        if (
            !this.policy ||
            !this.policy.captchaRequired ||
            !MainConfig.HCAPTCHA_SITE_KEY
        ) {
            return;
        }

        const existingScript = document.getElementById('hcaptcha-script');
        if (existingScript && window.hcaptcha) {
            this.renderCaptcha();
            return;
        }

        if (existingScript) {
            return;
        }

        const node = document.createElement('script');
        node.id = 'hcaptcha-script';
        node.type = 'text/javascript';
        node.async = true;
        node.onload = () => this.renderCaptcha();
        node.src =
            'https://hcaptcha.com/1/api.js?hl=' +
            (this.currentLanguage === 'fr' ? 'fr' : 'pt-BR');
        document.getElementsByTagName('head')[0].appendChild(node);
    }

    private resetCaptcha(): void {
        if (window.hcaptcha === null || !window.hcaptcha) {
            return;
        }
        this.user.captchaToken = null;
        try {
            window.hcaptcha.reset();
        } catch (_error) {
            // O widget pode não ter sido montado ainda.
        }
    }

    private renderCaptcha(): void {
        if (!window.hcaptcha || !MainConfig.HCAPTCHA_SITE_KEY) {
            return;
        }

        const captchaContainer = document.getElementById('h-captcha');
        if (!captchaContainer || captchaContainer.childNodes.length) {
            return;
        }

        window.hcaptcha.render('h-captcha', {
            sitekey: MainConfig.HCAPTCHA_SITE_KEY,
            callback: this.captchaCallback.bind(this),
            'expired-callback': this.resetCaptcha.bind(this),
            'error-callback': this.resetCaptcha.bind(this),
        });
    }

    private captchaCallback(token: string): void {
        this.user.captchaToken = token;
    }
}
